import {useState, useEffect, useCallback} from 'react';
import {licenseServiceGetQuota} from 'src/apis/autoGenerate/services';
import {LicenseQuota, LicenseQuotaByType} from 'src/apis/autoGenerate/types/license';
import {useOrgId} from './index';

// Shared across all useLicenseQuota() callers so a refetch in one place
// (e.g. after deleting units) updates License Usage cards elsewhere.
type QuotaListener = () => void;
let sharedOrgId: string | null = null;
let sharedQuota: LicenseQuota | null = null;
let sharedLoading = false;
let sharedError: string | null = null;
let fetchInFlight: Promise<void> | null = null;
const listeners = new Set<QuotaListener>();

const notifyQuotaListeners = () => {
  listeners.forEach(listener => listener());
};

const resetSharedQuota = () => {
  sharedQuota = null;
  sharedLoading = false;
  sharedError = null;
  fetchInFlight = null;
};

// Check if quota is in old flat format (has total_units at top level)
const isOldFormat = (quota: LicenseQuota | null): boolean => {
  if (!quota) return false;
  return 'total_units' in quota && typeof quota.total_units === 'number';
};

// Calculate aggregate stats across all license types (handles both old and new formats)
const calculateAggregateStats = (quota: LicenseQuota | null) => {
  if (!quota) {
    return {
      totalUnits: 0,
      usedUnits: 0,
      availableUnits: 0,
      activeLicensesCount: 0,
      hasUnlimited: false,
    };
  }

  // Old flat format: use values directly from quota
  if (isOldFormat(quota)) {
    const hasUnlimited =
      quota.has_unlimited !== undefined && typeof quota.has_unlimited === 'boolean'
        ? quota.has_unlimited
        : false;

    return {
      totalUnits: (quota.total_units as number) || 0,
      usedUnits: (quota.used_units as number) || 0,
      availableUnits: (quota.available_units as number) || 0,
      activeLicensesCount: (quota.active_licenses_count as number) || 0,
      hasUnlimited,
    };
  }

  // New nested format: aggregate from license types.
  // SIP is tracked separately (see extractSipQuota) because it caps users rather
  // than units, and must never feed into the unit-based isAtLimit lockout.
  const types = Object.keys(quota).filter(
    key => key !== 'organization_id' && key.toLowerCase() !== 'sip'
  );
  let totalUnits = 0;
  let usedUnits = 0;
  let availableUnits = 0;
  let activeLicensesCount = 0;
  let hasUnlimited = false;

  types.forEach(type => {
    const quotaData = quota[type];
    if (quotaData && typeof quotaData === 'object' && 'total_units' in quotaData) {
      const typeQuota = quotaData as LicenseQuotaByType;
      totalUnits += typeQuota.total_units || 0;
      usedUnits += typeQuota.used_units || 0;
      availableUnits += typeQuota.available_units || 0;
      activeLicensesCount += typeQuota.active_licenses_count || 0;
      if (typeQuota.has_unlimited) {
        hasUnlimited = true;
      }
    }
  });

  return {
    totalUnits,
    usedUnits,
    availableUnits,
    activeLicensesCount,
    hasUnlimited,
  };
};

// SIP quota lives under the `sip` key of the nested format. The old flat format
// predates SIP licenses and never carries one.
const extractSipQuota = (quota: LicenseQuota | null): LicenseQuotaByType | null => {
  if (!quota || isOldFormat(quota)) return null;

  const raw = quota['sip'];
  if (raw && typeof raw === 'object' && 'total_units' in raw) {
    return raw as LicenseQuotaByType;
  }

  return null;
};

export const useLicenseQuota = () => {
  const {orgId} = useOrgId();
  const [, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion(v => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const fetchQuota = useCallback(async () => {
    if (!orgId) return;

    if (sharedOrgId !== orgId) {
      sharedOrgId = orgId;
      resetSharedQuota();
      notifyQuotaListeners();
    }

    if (fetchInFlight) {
      await fetchInFlight;
      return;
    }

    // Keep showing the previous quota while refreshing so the card does not flash away.
    const isInitialLoad = sharedQuota == null;
    sharedLoading = isInitialLoad;
    sharedError = null;
    if (isInitialLoad) {
      notifyQuotaListeners();
    }

    fetchInFlight = (async () => {
      try {
        const response = await licenseServiceGetQuota();
        if (response.data?.data) {
          sharedQuota = response.data.data;
        }
      } catch (err: any) {
        sharedError = err.message || 'Failed to fetch license quota';
      } finally {
        sharedLoading = false;
        fetchInFlight = null;
        notifyQuotaListeners();
      }
    })();

    await fetchInFlight;
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (sharedOrgId !== orgId || !sharedQuota) {
      void fetchQuota();
    }
  }, [orgId, fetchQuota]);

  const quota = sharedOrgId === orgId ? sharedQuota : null;
  const loading = sharedOrgId === orgId ? sharedLoading : Boolean(orgId);
  const error = sharedOrgId === orgId ? sharedError : null;

  // Calculate aggregate stats from nested structure
  const aggregateStats = calculateAggregateStats(quota);
  const remainingUnits = aggregateStats.availableUnits;
  const usagePercentage =
    aggregateStats.totalUnits > 0
      ? (aggregateStats.usedUnits / aggregateStats.totalUnits) * 100
      : 0;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = remainingUnits < 0; // Only trigger when OVER limit (negative remaining)
  const hasUnlimited = aggregateStats.hasUnlimited;
  // At or over unit capacity — cannot add more rooms/PDK residents. Do not use
  // this for the full-app lockout (that stays isAtLimit / over-quota only).
  const isInsufficient =
    quota != null && !hasUnlimited && remainingUnits <= 0;

  // SIP quota. The backend uses -1 for both total and available to mean unlimited.
  const sipQuota = extractSipQuota(quota);
  const sipHasUnlimited = sipQuota?.has_unlimited === true;
  const sipTotalUnits = sipQuota?.total_units ?? 0;
  const sipUsedUnits = sipQuota?.used_units ?? 0;
  const sipAvailableUnits = sipQuota?.available_units ?? 0;
  const sipActiveLicensesCount = sipQuota?.active_licenses_count ?? 0;
  const sipUsagePercentage =
    !sipHasUnlimited && sipTotalUnits > 0 ? (sipUsedUnits / sipTotalUnits) * 100 : 0;

  // Whether the org owns any SIP license at all. Drives showing/hiding call-method UI.
  const hasSipLicense = sipHasUnlimited || sipTotalUnits > 0;
  // Whether another user can still be switched to SIP_PHONE right now.
  const hasSipQuotaAvailable = sipHasUnlimited || sipAvailableUnits > 0;

  return {
    quota,
    loading,
    error,
    remainingUnits,
    usagePercentage,
    isNearLimit,
    isAtLimit,
    isInsufficient,
    hasUnlimited,
    // Also return aggregate stats for components that need them
    totalUnits: aggregateStats.totalUnits,
    usedUnits: aggregateStats.usedUnits,
    availableUnits: aggregateStats.availableUnits,
    activeLicensesCount: aggregateStats.activeLicensesCount,
    // SIP quota, kept separate from the unit aggregate above
    sipQuota,
    sipTotalUnits,
    sipUsedUnits,
    sipAvailableUnits,
    sipActiveLicensesCount,
    sipHasUnlimited,
    sipUsagePercentage,
    hasSipLicense,
    hasSipQuotaAvailable,
    refetch: fetchQuota,
  };
};
